import { useQuery } from '@tanstack/react-query'
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import axios from 'axios'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'

// TODO(B2B API generation): Replace this hand-written adapter with the
// generated User service client after its public OpenAPI contract is available.
export const OWN_PROFILE_PATH = '/users/api/v1/users/me'

export type UserState = 'draft' | 'registered'

export type ActiveKyc = {
  correlationId: string
  runStartedAt: string
  evidenceRef: string | null
  validUntil: string | null
}

export type OwnProfile = {
  sub: string
  state: UserState
  email: string | null
  firstName: string | null
  lastName: string | null
  phone: string | null
  promoCode: string | null
  registeredAt: string | null
  verified: boolean
  activeKyc: ActiveKyc | null
}

export type UserServiceError = {
  code: string
  context: Record<string, unknown> | null
  message?: string
}

export const getOwnProfile = (
  options?: AxiosRequestConfig
): Promise<AxiosResponse<OwnProfile>> => axios.get(OWN_PROFILE_PATH, options)

export const getOwnProfileQueryKey = () => [OWN_PROFILE_PATH] as const

export const getOwnProfileQueryOptions = <
  TData = Awaited<ReturnType<typeof getOwnProfile>>,
  TError = AxiosError<UserServiceError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getOwnProfile>>, TError, TData>
  >
  axios?: AxiosRequestConfig
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getOwnProfileQueryKey()
  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOwnProfile>>> = ({
    signal,
  }) => getOwnProfile({ signal, ...axiosOptions })

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOwnProfile>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export const useGetOwnProfile = <
  TData = Awaited<ReturnType<typeof getOwnProfile>>,
  TError = AxiosError<UserServiceError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getOwnProfile>>, TError, TData>
  >
  axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getOwnProfileQueryOptions(options)
  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey
  }

  query.queryKey = queryOptions.queryKey

  return query
}
